import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELD_TYPES = [
  { v: "text", l: "Text" }, { v: "textarea", l: "Text area" }, { v: "number", l: "Number" },
  { v: "date", l: "Date" }, { v: "dropdown", l: "Dropdown" }, { v: "multiselect", l: "Multi-select" },
  { v: "checkbox", l: "Checkbox (Yes/No)" }, { v: "phone", l: "Phone" },
];
const DIMENSIONS = [
  { v: "main_category", l: "Main Category" }, { v: "sub_category", l: "Sub Category" },
  { v: "issue_type", l: "Issue Type" }, { v: "department", l: "Department" },
  { v: "channel", l: "Channel" }, { v: "ticket_type", l: "Ticket Type" },
];
const OPERATORS = [{ v: "in", l: "is any of" }, { v: "equals", l: "is" }, { v: "not_equals", l: "is not" }];
const LOOKUP_SOURCES = [
  { v: "branches", l: "Branches / Stores" }, { v: "departments", l: "Departments" },
  { v: "contact_modes", l: "Contact Modes" }, { v: "languages", l: "Languages" },
  { v: "case_types", l: "Case Types" }, { v: "channels", l: "Channels" },
];

const BLANK = {
  label: "", key: "", field_type: "text", required: false, placeholder: "", help_text: "",
  order: 0, active: true, options_source: "static", static_options: [], lookup_source: null,
  conditions: { match: "all", rules: [] }, validation: {},
};

export default function DynamicFieldDialog({ open, onOpenChange, field, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [optInput, setOptInput] = useState("");
  // dimension → [{value,label}] for condition value pickers
  const [dimOpts, setDimOpts] = useState({});

  const isEdit = !!field?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(field?.id ? { ...BLANK, ...field } : BLANK);
    setOptInput("");
    // load condition value sources
    Promise.all([
      api.get("/main-categories"), api.get("/sub-categories"), api.get("/issue-types"),
      api.get("/departments"), api.get("/channels"), api.get("/ticket-types"),
    ]).then(([mc, sc, it, dep, ch, tt]) => {
      setDimOpts({
        main_category: mc.data.map((x) => ({ value: x.id, label: x.name })),
        sub_category: sc.data.map((x) => ({ value: x.id, label: x.name })),
        issue_type: it.data.map((x) => ({ value: x.id, label: x.name })),
        department: dep.data.map((x) => ({ value: x.id, label: x.name })),
        channel: ch.data.map((x) => ({ value: x.name, label: x.name })),
        ticket_type: tt.data.map((x) => ({ value: x.name || x.key, label: x.name || x.label || x.key })),
      });
    }).catch(() => {});
  }, [open, field]);

  const addRule = () => set("conditions", { ...form.conditions, rules: [...form.conditions.rules, { dimension: "main_category", operator: "in", values: [] }] });
  const updRule = (i, patch) => {
    const rules = form.conditions.rules.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    set("conditions", { ...form.conditions, rules });
  };
  const delRule = (i) => set("conditions", { ...form.conditions, rules: form.conditions.rules.filter((_, idx) => idx !== i) });
  const toggleRuleValue = (i, val) => {
    const r = form.conditions.rules[i];
    const has = r.values.includes(val);
    updRule(i, { values: has ? r.values.filter((x) => x !== val) : [...r.values, val] });
  };

  const addStaticOpt = () => {
    const v = optInput.trim();
    if (v && !form.static_options.includes(v)) set("static_options", [...form.static_options, v]);
    setOptInput("");
  };

  const save = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    if (form.field_type === "dropdown" || form.field_type === "multiselect") {
      if (form.options_source === "static" && form.static_options.length === 0)
        return toast.error("Add at least one option or switch to a master-data source");
      if (form.options_source === "lookup" && !form.lookup_source)
        return toast.error("Select a master-data source");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) await api.patch(`/dynamic-fields/${field.id}`, payload);
      else await api.post("/dynamic-fields", payload);
      toast.success(isEdit ? "Field updated" : "Field created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const needsOptions = form.field_type === "dropdown" || form.field_type === "multiselect";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dynamic-field-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit field" : "New dynamic field"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Label *</Label>
              <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Order No." className="mt-1" data-testid="df-label" />
              {isEdit && <p className="text-[10px] text-slate-400 mt-1 font-mono">key: {form.key}</p>}
            </div>
            <div>
              <Label className="text-xs">Field type</Label>
              <Select value={form.field_type} onValueChange={(v) => set("field_type", v)}>
                <SelectTrigger className="mt-1" data-testid="df-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div className="flex items-center gap-2">
              <Switch checked={form.required} onCheckedChange={(v) => set("required", v)} data-testid="df-required" />
              <Label className="text-xs">Mandatory</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="df-active" />
              <Label className="text-xs">Active</Label>
            </div>
            <div>
              <Label className="text-xs">Order</Label>
              <Input type="number" value={form.order} onChange={(e) => set("order", Number(e.target.value))} className="mt-1" data-testid="df-order" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Placeholder</Label>
              <Input value={form.placeholder} onChange={(e) => set("placeholder", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Help text</Label>
              <Input value={form.help_text} onChange={(e) => set("help_text", e.target.value)} className="mt-1" />
            </div>
          </div>

          {needsOptions && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <Label className="text-xs">Options source</Label>
                <Select value={form.options_source} onValueChange={(v) => set("options_source", v)}>
                  <SelectTrigger className="h-8 w-48" data-testid="df-options-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="static">Custom list</SelectItem>
                    <SelectItem value="lookup">Master data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.options_source === "lookup" ? (
                <Select value={form.lookup_source || ""} onValueChange={(v) => set("lookup_source", v)}>
                  <SelectTrigger className="h-8" data-testid="df-lookup-source"><SelectValue placeholder="Choose master data…" /></SelectTrigger>
                  <SelectContent>{LOOKUP_SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <Input value={optInput} onChange={(e) => setOptInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStaticOpt(); } }}
                      placeholder="Type an option, press Enter" className="h-8" data-testid="df-option-input" />
                    <Button type="button" size="sm" variant="outline" onClick={addStaticOpt}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.static_options.map((o) => (
                      <span key={o} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5">
                        {o}
                        <button type="button" onClick={() => set("static_options", form.static_options.filter((x) => x !== o))}><X className="w-3 h-3 text-slate-400" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Conditions */}
          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold">Show this field when</Label>
                <Select value={form.conditions.match} onValueChange={(v) => set("conditions", { ...form.conditions, match: v })}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid="df-match"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ALL match</SelectItem>
                    <SelectItem value="any">ANY match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addRule} data-testid="df-add-rule">
                <Plus className="w-3.5 h-3.5 mr-1" />Rule
              </Button>
            </div>
            {form.conditions.rules.length === 0 && (
              <p className="text-[11px] text-slate-400">No rules — this field will always show. Add a rule to make it conditional.</p>
            )}
            {form.conditions.rules.map((r, i) => (
              <div key={i} className="border border-slate-100 rounded-md p-2 space-y-2 bg-slate-50/50" data-testid={`df-rule-${i}`}>
                <div className="flex items-center gap-2">
                  <Select value={r.dimension} onValueChange={(v) => updRule(i, { dimension: v, values: [] })}>
                    <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{DIMENSIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={r.operator} onValueChange={(v) => updRule(i, { operator: v })}>
                    <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPERATORS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => delRule(i)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(dimOpts[r.dimension] || []).map((o) => {
                    const on = r.values.includes(o.value);
                    return (
                      <button key={o.value} type="button" onClick={() => toggleRuleValue(i, o.value)}
                        className={`text-xs px-2 py-0.5 rounded-full border transition ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {o.label}
                      </button>
                    );
                  })}
                  {(dimOpts[r.dimension] || []).length === 0 && <span className="text-[11px] text-slate-400">No values available</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="df-save">
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
